import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { withRouter } from 'react-router-dom';
import Classnames from 'classnames';
import Modal from 'react-responsive-modal';
import { deletePersonalStrategy, actionTypes } from '../../../actions/data';
import { addScreenedFund, removeScreendFund, removeSearchTicker } from '../../../actions';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faClock, faEdit, faInfo } from '@fortawesome/free-solid-svg-icons';
import { formatDate, formatTime } from '../../../common';
import './StrategyRow.css';
import i18n from '../../../i18n';
import StrategyEditor from 'wt-react/dist/components/StrategyEditor/StrategyEditor';

library.add(faClock, faInfo, faEdit);

const useCheckboxHandler = (personalStrategy, savingStrategy) => {
  const dispatch = useDispatch();
  const screenedFunds = useSelector(state => state.params.screenedFunds);
  const [isChecked, setIsChecked] = useState(false);

  const handleCheckbox = useCallback((strategyToEdit) => {
    const { id, name, periodicity, holdings } = strategyToEdit;
    const externalID = id;
    const ticker = holdings[holdings.length - 1].tickers.map(t => `${t}`).join(',');
    const type = 'user_model';
    const rebalance_fq = periodicity;
    const filteredFund = { id, externalID, ticker, rebalance_fq, name, type };
    const foundFund = screenedFunds?.find(fund => fund.id === filteredFund.id);

    if (foundFund) {
      dispatch(removeScreendFund(externalID));
      dispatch(removeSearchTicker(externalID));
      setIsChecked(false);
    } else {
      dispatch(addScreenedFund(filteredFund));
      setIsChecked(true);
    }
  }, [dispatch, screenedFunds, savingStrategy]);

  useEffect(() => {
    const foundFund = screenedFunds?.find(fund => fund.id === personalStrategy?.id);
    setIsChecked(!!foundFund);
  }, [personalStrategy?.id, screenedFunds]);
    

  return { isChecked, handleCheckbox };
};

const DeleteModal = ({ isOpen, onClose, onDelete, strategyName }) => (
  <Modal open={isOpen} onClose={onClose} showCloseIcon={false} classNames={{ overlay: 'modal-overlay', modal: 'delete-modal', closeIcon: 'custom-closeIcon' }} center>
    <div>
      <br />
      {i18n.t('confirm-delete')}
      <strong>{strategyName}</strong>?
      <br />
      <br />
      <div className='align-right'>
        <button className='portfolio-button' onClick={onDelete}>
          {i18n.t('delete')}
        </button>
        <button className='portfolio-button' onClick={onClose}>
          {i18n.t('cancel')}
        </button>
      </div>
    </div>
  </Modal>
);

function UpdatedLast({ savingStrategy, personalStrategy, openMenu }) {
  if (openMenu) {
    return null;
  }

  const defaultDate = new Date('2024-07-15T15:00:00-04:00'); // Post migration to MyPortfilo holdingsLastUpdateDate is null. Defaulted to July 15, 2024, 3 PM EST
  
  return (
    <div className='last-updated'>
      {savingStrategy ? 
        '---' : 
        `${formatDate(personalStrategy?.holdingsLastUpdateDate ?? defaultDate)} ${formatTime(personalStrategy?.holdingsLastUpdateDate ?? defaultDate)}`
      }
    </div>
  );
}

const CheckboxGroup = ({ isChecked, handleCheckbox, personalStrategy, savingStrategy }) => {
  let iconColor;

  if (isChecked) {
    iconColor = '#0047bb';
  } else {
    iconColor = personalStrategy?.type === 'COD' ? '#050139' : '#0face7';
  }

  return (
    <div className='check-box-group' onChange={() => handleCheckbox(personalStrategy)}>
      <FontAwesomeIcon
        style={{
          color: iconColor,
          marginLeft: '10px',
          cursor: !savingStrategy ? 'pointer' : null
        }}
        size='2x'
        icon='plus-circle'
        onClick={() => handleCheckbox(personalStrategy)}
      />
    </div>
  );
};


const StrategyName = ({ personalStrategy }) => (
  <div className='name'>
    {personalStrategy?.name}
  </div>
);


const Statistics = ({ savingStrategy, personalStrategy, openMenu }) => {
  const lastHolding = personalStrategy?.holdings?.[personalStrategy?.holdings.length - 1];
  const tickersCount = lastHolding?.tickers?.length ?? null;

  return (
    <div className='statistics'>{savingStrategy ? '---' : personalStrategy && !openMenu && tickersCount}</div>
  );
};

const MenuButtons = ({ savingStrategy, personalStrategy, params, handleDeleteClick, setSavingStrategyInProgress }) => (
  <div className='menu-buttons'>
    {!savingStrategy ? (
      <>
        <div className='menu-button'>
          <StrategyEditor
            key={'new-strategy-editor-1'}
            type={'update'}
            token={params.token}
            showEditor={true}
            clientEmail={params.advisorEmail}
            dataspanApiUrl={`${process.env.REACT_APP_API_URL}`}
            checkTickersUrl={`${process.env.REACT_APP_DPD_API_URL}`}
            typeHeadPlaceholder={'Search By Ticker or Name'}
            strategyToEdit={personalStrategy}
            savedStrategyCallBack={setSavingStrategyInProgress}
            showInfoModal={false}
            closeModalOnSave={true}
            disableUpdate={personalStrategy.type === 'COD'}
          />
        </div>
        <div className='menu-button' onClick={handleDeleteClick}>
          <div className='cross'>
            <strong>✕</strong>
          </div>
        </div>
      </>
    ) : (
      <div className='waiting' />
    )}
  </div>
);

const StrategyRow = ({ personalStrategy, saveInProgress = false, openMenu = false, striped = false }) => {
  const dispatch = useDispatch();
  const params = useSelector(state => state.params);
  const [savingStrategy, setSavingStrategy] = useState(saveInProgress);
  const [startDeletion, setStartDeletion] = useState(false);

  const { isChecked, handleCheckbox } = useCheckboxHandler(personalStrategy, savingStrategy);

  const setSavingStrategyInProgress = (value, isSaving) => {
    setSavingStrategy(isSaving);
    if (!isSaving && value) {
      dispatch({
        type: actionTypes.UPDATE_STRATEGY,
        value: value,
      });
    }
  };

  const handleDeleteClick = () => {
    setStartDeletion(true);
  };

  const handleCloseDeleteModal = () => {
    setStartDeletion(false);
  };

  const handleDeleteConfirmation = useCallback((idToDelete) => {
    setStartDeletion(false);
    dispatch(deletePersonalStrategy(idToDelete));
  }, [dispatch]);

  return (
    <div>
      <div className={Classnames('strategyBlendRow', { striped: striped && !openMenu }, { opened: openMenu })}>
        <CheckboxGroup isChecked={isChecked} handleCheckbox={handleCheckbox} personalStrategy={personalStrategy} savingStrategy={savingStrategy} />
        <StrategyName personalStrategy={personalStrategy} />
        <UpdatedLast savingStrategy={savingStrategy} personalStrategy={personalStrategy} openMenu={openMenu} />
        <Statistics savingStrategy={savingStrategy} personalStrategy={personalStrategy} openMenu={openMenu} />
        <MenuButtons savingStrategy={savingStrategy} personalStrategy={personalStrategy} params={params} handleDeleteClick={handleDeleteClick} setSavingStrategyInProgress={setSavingStrategyInProgress} />
      </div>

      {startDeletion && (
        <DeleteModal
          isOpen={startDeletion}
          onClose={handleCloseDeleteModal}
          onDelete={() => handleDeleteConfirmation(personalStrategy?.id)}
          strategyName={personalStrategy?.name}
        />
      )}
    </div>
  );
};

export default withRouter(StrategyRow);
